"use client";

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Switch } from "@package/subframe/components/Switch";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherGripVertical,
  FeatherPlus,
  FeatherTrash2,
  FeatherX,
} from "@subframe/core";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import {
  AddQuestionDtoQuestionType,
  useCreateTemplate,
} from "@/api/hooks/useTemplates";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  getQuestionTypeLabel,
  QUESTION_TYPE_LABELS,
} from "@/lib/checklist-utils";
import { generateClientId } from "@/lib/offline/db";

interface ChecklistItem {
  id: string;
  label: string;
  isRequired: boolean;
  questionType: AddQuestionDtoQuestionType;
}

interface Template {
  id: string;
  name: string;
  description: string | null;
  items: { id: string; label: string; questionType?: AddQuestionDtoQuestionType }[];
}

interface GlobalTemplateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: Template | null;
}

const templateSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters"),
  description: z
    .string()
    .trim()
    .max(500, "Description must be less than 500 characters")
    .optional(),
  items: z
    .array(
      z.object({
        id: z.string(),
        label: z
          .string()
          .trim()
          .min(1, "Item label is required")
          .max(200, "Label must be less than 200 characters"),
        isRequired: z.boolean(),
      }),
    )
    .min(1, "At least one checklist item is required"),
});

function SortableChecklistItem({
  item,
  index,
  error,
  onUpdate,
  onUpdateType,
  onRemove,
  canRemove,
  disabled,
}: {
  item: ChecklistItem;
  index: number;
  error?: string;
  onUpdate: (
    id: string,
    field: keyof ChecklistItem,
    value: string | boolean,
  ) => void;
  onUpdateType: (id: string, type: AddQuestionDtoQuestionType) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
  disabled?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-3 rounded-md border border-solid border-secondary-beige-200 bg-neutral-50 px-4 py-3"
    >
      {!disabled && (
        <button
          type="button"
          className="mt-2 cursor-grab active:cursor-grabbing text-subtext-color hover:text-default-font"
          {...attributes}
          {...listeners}
        >
          <FeatherGripVertical className="h-5 w-5" />
        </button>
      )}
      <div className="flex grow shrink-0 basis-0 flex-col items-start gap-3">
        <div className="flex w-full items-center gap-3">
          <span className="text-body-bold font-body-bold text-subtext-color w-6">
            {index + 1}.
          </span>
          <TextField
            className="h-auto grow shrink-0 basis-0"
            variant="outline"
            error={!!error}
            disabled={disabled}
          >
            <TextField.Input
              placeholder="Enter checklist item..."
              value={item.label}
              onChange={(e) => onUpdate(item.id, "label", e.target.value)}
              disabled={disabled}
            />
          </TextField>
        </div>
        {error && (
          <span className="text-caption font-caption text-error-700 pl-9">
            {error}
          </span>
        )}
        <div className="flex items-center gap-3 pl-9">
          <Switch
            checked={item.isRequired}
            onCheckedChange={(checked) =>
              onUpdate(item.id, "isRequired", checked)
            }
            disabled={disabled}
          />
          <span className="text-body font-body text-subtext-color">
            Required
          </span>
        </div>
        <div className="flex items-center gap-2 pl-9">
          <span className="text-body font-body text-subtext-color">Type:</span>
          {disabled ? (
            <span className="text-caption font-caption px-2 py-1 rounded border border-neutral-600 text-neutral-900 bg-white">
              {getQuestionTypeLabel(item.questionType)}
            </span>
          ) : (
            Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`text-caption font-caption px-2 py-1 rounded border transition-colors ${
                  item.questionType === value
                    ? "border-neutral-600 text-neutral-900 bg-white"
                    : "border-secondary-beige-200 text-subtext-color bg-white hover:border-neutral-400"
                }`}
                onClick={() => onUpdateType(item.id, value as AddQuestionDtoQuestionType)}
              >
                {label}
              </button>
            ))
          )}
        </div>
      </div>
      {!disabled && (
        <IconButton
          variant="neutral-tertiary"
          size="medium"
          icon={<FeatherTrash2 />}
          onClick={() => onRemove(item.id)}
          disabled={!canRemove}
        />
      )}
    </div>
  );
}

export function GlobalTemplateFormDialog({
  open,
  onOpenChange,
  template,
}: GlobalTemplateFormDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const createMutation = useCreateTemplate();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || "");
      // Map template items to include isRequired (default true for existing items)
      setItems(
        (template.items || []).map((item) => ({
          id: item.id,
          label: item.label,
          isRequired: true,
          questionType: item.questionType ?? AddQuestionDtoQuestionType.YES_NO,
        })),
      );
    } else {
      setName("");
      setDescription("");
      setItems([{ id: generateClientId(), label: "", isRequired: true, questionType: AddQuestionDtoQuestionType.YES_NO }]);
    }
    setErrors({});
  }, [template, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For editing, show a message that it's view-only for now
    if (template) {
      toast.info(
        "Template editing is not yet supported. You can view the template details.",
      );
      onOpenChange(false);
      return;
    }

    const result = templateSchema.safeParse({ name, description, items });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err) => {
        const path = err.path.join(".");
        fieldErrors[path] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setIsSaving(true);

    try {
      // Create the template with questions in a single request
      // No organisationId for global templates
      await createMutation.mutateAsync({
        name,
        description: description || undefined,
        questions: items.map((item, index) => ({
          questionText: item.label,
          questionType: item.questionType,
          isRequired: item.isRequired,
          orderIndex: index,
        })),
      });

      toast.success("Global template created successfully");
      onOpenChange(false);
    } catch (error) {
      toast.error(
        "Failed to create template: " +
          (error instanceof Error ? error.message : "Unknown error"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const addItem = () => {
    setItems([
      ...items,
      { id: generateClientId(), label: "", isRequired: true, questionType: AddQuestionDtoQuestionType.YES_NO },
    ]);
  };

  const updateItemType = (id: string, type: AddQuestionDtoQuestionType) => {
    setItems(items.map((item) => item.id === id ? { ...item, questionType: type } : item));
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter((item) => item.id !== id));
    }
  };

  const updateItem = (
    id: string,
    field: keyof ChecklistItem,
    value: string | boolean,
  ) => {
    setItems(
      items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-2xl max-h-[90vh] overflow-y-auto p-0"
      >
        <DialogTitle className="sr-only">
          {template ? "View Global Template" : "Create Global Template"}
        </DialogTitle>
        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {template ? "View Global Template" : "Create Global Template"}
              </span>
              <span className="text-body font-body text-subtext-color">
                {template
                  ? "View the global template details and items."
                  : "Create a new global template that will be available to all organisations."}
              </span>
            </div>
            <IconButton
              variant="neutral-tertiary"
              size="medium"
              icon={<FeatherX />}
              onClick={() => onOpenChange(false)}
            />
          </div>

          {/* Form Content */}
          <div className="flex flex-col gap-6 px-6 py-6">
            {/* Template Details */}
            <div className="flex flex-col gap-4">
              <TextField
                className="h-auto w-full"
                size="large"
                label="Template name"
                error={!!errors.name}
                helpText={errors.name}
                disabled={!!template}
              >
                <TextField.Input
                  placeholder="e.g., Daily Safety Check"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!!template}
                />
              </TextField>

              <TextArea
                className="h-auto w-full"
                label="Description (optional)"
                error={!!errors.description}
                helpText={errors.description}
              >
                <TextArea.Input
                  placeholder="Describe what this checklist is for..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!!template}
                />
              </TextArea>
            </div>

            {/* Checklist Items */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="text-body-bold font-body-bold text-default-font">
                  Checklist items
                </span>
                {!template && (
                  <Button
                    type="button"
                    variant="neutral-secondary"
                    icon={<FeatherPlus />}
                    onClick={addItem}
                  >
                    Add item
                  </Button>
                )}
              </div>

              {errors.items && (
                <span className="text-caption font-caption text-error-700">
                  {errors.items}
                </span>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={items.map((i) => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col gap-3">
                    {items.map((item, index) => (
                      <SortableChecklistItem
                        key={item.id}
                        item={item}
                        index={index}
                        error={errors[`items.${index}.label`]}
                        onUpdate={updateItem}
                        onUpdateType={updateItemType}
                        onRemove={removeItem}
                        canRemove={items.length > 1 && !template}
                        disabled={!!template}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-solid border-secondary-beige-200 px-6 py-4">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              size="large"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : template ? "Close" : "Create Template"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
